import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { shopifyAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useTenantUrl } from '../hooks/useTenantUrl';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { tenantNavigate } = useTenantUrl();
  const { currentTenant } = useAuth();
  const [order, setOrder] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await shopifyAPI.getOrder(parseInt(id!));
      setOrder(response.data.data.order || response.data.data);
      setTransactions(response.data.data.transactions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleResync = async () => {
    try {
      setResyncing(true);
      setResyncMessage(null);
      setError(null);
      const response = await shopifyAPI.resyncOrder(parseInt(id!));
      setOrder(response.data.data.order || response.data.data);
      setTransactions(response.data.data.transactions || []);
      setResyncMessage(response.data.message || 'Order resynced successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resync order');
    } finally {
      setResyncing(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading order details...</div>;
  }

  if (error) {
    return (
      <div>
        <div className="error">{error}</div>
        <button className="btn btn-secondary" onClick={() => tenantNavigate('/orders')}>
          Back to Orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <div className="error">Order not found</div>
        <button className="btn btn-secondary" onClick={() => tenantNavigate('/orders')}>
          Back to Orders
        </button>
      </div>
    );
  }

  const totalPayments = transactions
    .filter(t => t.transaction_type === 'payment')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalRefunds = transactions
    .filter(t => t.transaction_type === 'refund')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h1>Order {order.name || `#${order.order_number}`}</h1>
          {currentTenant?.shopify_shop_name && (
            <a
              href={`https://admin.shopify.com/store/${currentTenant.shopify_shop_name}/orders/${order.shopify_order_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              View in Shopify →
            </a>
          )}
          <button 
            className="btn btn-secondary" 
            onClick={handleResync}
            disabled={resyncing}
            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
          >
            {resyncing ? '🔄 Resyncing...' : '🔄 Resync from Shopify'}
          </button>
        </div>
        <button className="btn btn-secondary" onClick={() => tenantNavigate('/orders')}>
          Back to Orders
        </button>
      </div>

      {resyncMessage && (
        <div style={{ 
          padding: '0.75rem 1rem', 
          backgroundColor: '#c6f6d5', 
          borderRadius: '0.375rem', 
          marginBottom: '1rem',
          color: '#22543d'
        }}>
          ✓ {resyncMessage}
        </div>
      )}

      {/* Order Details Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2>Order Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Order Number
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600, fontFamily: 'monospace' }}>
              {order.name || `#${order.order_number}`}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Order Date
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600 }}>
              {format(new Date(order.order_date), 'MMM dd, yyyy HH:mm')}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Customer Email
            </div>
            <div style={{ fontSize: '1.125rem' }}>
              {order.customer_email || 'N/A'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Financial Status
            </div>
            <div>
              <span className={`status-badge ${
                order.financial_status === 'paid' ? 'status-reconciled' :
                order.financial_status === 'refunded' ? 'status-discrepancy' :
                'status-pending'
              }`}>
                {order.financial_status}
              </span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Fulfillment Status
            </div>
            <div>
              <span className={`status-badge ${
                order.fulfillment_status === 'fulfilled' ? 'status-reconciled' :
                order.fulfillment_status === 'partial' ? 'status-pending' :
                'status-unmapped'
              }`}>
                {order.fulfillment_status || 'unfulfilled'}
              </span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Currency
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600 }}>
              {order.currency}
            </div>
          </div>
        </div>
      </div>

      {/* Order Amounts Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2>Amounts</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Subtotal
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              ${Number(order.subtotal_amount).toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Tax
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              ${Number(order.tax_amount || 0).toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Shipping
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              ${Number(order.shipping_amount || 0).toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Discounts
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#f56565' }}>
              -${Number(order.discount_amount || 0).toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Order Total
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#4299e1' }}>
              ${Number(order.total_amount).toFixed(2)}
            </div>
          </div>

          {Number(order.refund_amount || 0) > 0 && (
            <>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
                  Returns
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#ed8936' }}>
                  -${Number(order.refund_amount || 0).toFixed(2)}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
                  Effective Total
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#48bb78' }}>
                  ${(Number(order.total_amount) - Number(order.refund_amount || 0)).toFixed(2)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Returns/Refunds Card - Show refund dates */}
      {(order.refunds && order.refunds.length > 0) && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2>Returns / Refunds</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {order.refunds.map((refund: { date: string; amount: number }, index: number) => (
                  <tr key={index}>
                    <td>{format(new Date(refund.date), 'MMM dd, yyyy HH:mm')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#ed8936' }}>
                      -${Number(refund.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  <td>Total Returns</td>
                  <td style={{ textAlign: 'right', color: '#ed8936' }}>
                    -${Number(order.refund_amount || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ 
            marginTop: '0.75rem', 
            fontSize: '0.875rem', 
            color: '#718096',
            fontStyle: 'italic'
          }}>
            Note: Return dates are used for date-aware calculations in the Unpaid Orders report.
          </div>
        </div>
      )}

      {/* Tax Lines Card */}
      {order.raw_data?.tax_lines && order.raw_data.tax_lines.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2>Tax Breakdown</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Tax Type</th>
                  <th>Rate</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {order.raw_data.tax_lines.map((taxLine: any, index: number) => (
                  <tr key={index}>
                    <td>{taxLine.title || 'Tax'}</td>
                    <td>{taxLine.rate_percentage ? `${taxLine.rate_percentage}%` : (taxLine.rate ? `${(taxLine.rate * 100).toFixed(2)}%` : 'N/A')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      ${Number(taxLine.price || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  <td colSpan={2}>Total Tax</td>
                  <td style={{ textAlign: 'right' }}>
                    ${Number(order.tax_amount || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction Summary Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2>Transaction Summary</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Total Payments
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#48bb78' }}>
              ${totalPayments.toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Total Refunds
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#f56565' }}>
              ${totalRefunds.toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Net Amount
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#4299e1' }}>
              ${(totalPayments - totalRefunds).toFixed(2)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>
              Transaction Count
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              {transactions.length}
            </div>
          </div>
        </div>

        {/* Reconciliation Note */}
        {(() => {
          const effectiveTotal = Number(order.total_amount) - Number(order.refund_amount || 0);
          const netPayments = totalPayments - totalRefunds;
          const difference = Math.abs(effectiveTotal - netPayments);
          const hasReturns = Number(order.refund_amount || 0) > 0;
          
          if (hasReturns && difference < 0.01) {
            return (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: '#c6f6d5', 
                borderRadius: '0.375rem',
                color: '#22543d',
                fontSize: '0.875rem'
              }}>
                ✓ <strong>Reconciled:</strong> Net payments (${netPayments.toFixed(2)}) match effective order total after returns (${effectiveTotal.toFixed(2)})
              </div>
            );
          } else if (hasReturns) {
            return (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: '#fefcbf', 
                borderRadius: '0.375rem',
                color: '#744210',
                fontSize: '0.875rem'
              }}>
                ℹ️ <strong>Note:</strong> This order has ${Number(order.refund_amount).toFixed(2)} in returns. 
                Expected payment: ${effectiveTotal.toFixed(2)} (Order Total ${Number(order.total_amount).toFixed(2)} - Returns ${Number(order.refund_amount).toFixed(2)})
              </div>
            );
          } else if (Math.abs(Number(order.total_amount) - netPayments) > 0.01 && order.financial_status === 'paid') {
            return (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: '#fed7d7', 
                borderRadius: '0.375rem',
                color: '#742a2a',
                fontSize: '0.875rem'
              }}>
                ⚠️ <strong>Discrepancy:</strong> Order is marked as paid but net payments (${netPayments.toFixed(2)}) don't match order total (${Number(order.total_amount).toFixed(2)}). 
                Try resyncing to fetch any missing returns or transactions.
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Transactions Table */}
      <div className="card">
        <h2>Transactions</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Shopify TX ID</th>
                <th>Date & Time</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Gateway</th>
                <th>Status</th>
                <th>Expected Payout</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                    No transactions found for this order
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#718096' }}>
                        {transaction.shopify_transaction_id || 'N/A'}
                      </span>
                    </td>
                    <td>{format(new Date(transaction.transaction_date), 'MMM dd, yyyy HH:mm')}</td>
                    <td>
                      <span className={`status-badge ${
                        transaction.transaction_type === 'payment' ? 'status-reconciled' : 'status-pending'
                      }`}>
                        {transaction.transaction_type}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      ${Number(transaction.amount).toFixed(2)}
                    </td>
                    <td>{transaction.payment_method_name || 'N/A'}</td>
                    <td>{transaction.gateway || 'N/A'}</td>
                    <td>{transaction.status}</td>
                    <td>
                      {transaction.expected_payout_date
                        ? format(new Date(transaction.expected_payout_date), 'MMM dd, yyyy')
                        : 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;

